// Допущения под ответом: «Исходил из: … Изменить». SMY-497 · 26.10.2026
//
// ИСТОЧНИК — ТАБЛИЦА ТЕКСТОВ, А НЕ ЭТОТ ФАЙЛ. Строки показа и заготовки правок
// пишет ux-writer в docs/copy/household-task-catalog.md, решения владельца по
// ним принимаются там. Здесь копия, которую читают клиенты; сверяет её тест
// рядом — по каждой строке таблицы и по списку «Типы без блока». Правишь
// таблицу — правишь и здесь, тем же коммитом: файл стоит в исключениях фильтра
// путей CI, так что правка одной таблицы CI запускает.
//
// ПОЧЕМУ НЕ `TASK_TYPES[тип].assumptions`. Там рабочие строки PRD для рецепта
// и классификатора — с обратными кавычками, `[вписать]` и условиями «если не
// сказано…». Человеку показываются только строки, верные при ЛЮБОМ запросе
// типа, в том числе когда значение он назвал сам (решение владельца В1-C): у
// большинства типов таких нет вовсе, и блока у них нет.
//
// ПОЧЕМУ В ЯДРЕ. Клиентов три, и строка, место блока и склейка заготовки с
// набранным должны совпасть у всех. Решает, ЕСТЬ ли блок, клиент по ленте —
// но этой одной функцией (`assumptionsRunId`), а не своим правилом.
//
// ЗАГОТОВКА — НАЧАЛО ПРАВКИ, А НЕ КОМАНДА. Уходит обычной отправкой (В3-A):
// у карточки документа — правкой (`followUpFor` обязан сказать `free_edit`,
// это сторожит тест по каждой заготовке с типичными дописками), у ответа —
// бесплатным уточнением или, если запас кончился, платным запуском с
// подтверждением (В4-A). Своего правила списания у «Изменить» нет: блок только
// НАЗЫВАЕТ цену, когда правка спишется (В4-Б, 27.10.2026, `assumptionPaidUnit`).
//
// ОТВЕРГНУТО 26.10.2026: показывать допущение, только если слот не назван
// (В1-B) — нужна память о названных слотах на запуске; отдельной задачей.

import { lastChargedIndex, nextSendCost } from './artifact.ts'
import { appendToField } from './text.ts'
import { formatUnit, type UnitId } from './units.ts'
import type { TaskTypeId } from './task-types.ts'

/** Одна строка блока: что показали и с чего начинается правка в поле. */
export interface Assumption {
  /** Строка показа, с маленькой буквы — склеивается через «; ». */
  text: string
  /** Начало правки для поля ввода: повелительный глагол, человек дописывает. */
  edit: string
}

/** Строки блока по типам. Нет типа в списке — блока у него нет. */
export const ASSUMPTIONS: Partial<Record<TaskTypeId, readonly Assumption[]>> = {
  consumer_claim: [{ text: 'закон о правах потребителей', edit: 'Перепиши претензию: продавец —' }],
  service_complaint: [{ text: 'деловой стиль', edit: 'Смени стиль жалобы:' }],
  uk_application: [{ text: 'от собственника или нанимателя', edit: 'Перепиши заявление от имени:' }],
  official_letter: [{ text: 'деловой стиль', edit: 'Смени стиль письма:' }],
  reply_to_letter: [{ text: 'ответ на каждый вопрос письма', edit: 'Оставь в ответе только:' }],
  personal_application: [{ text: 'заявление от твоего имени', edit: 'Перепиши заявление от имени:' }],
  simple_contract: [
    { text: 'стороны — частные лица', edit: 'Перепиши договор: одна из сторон —' },
    { text: 'обычные условия такого договора', edit: 'Добавь в договор условие:' },
  ],
  compare_products: [{ text: 'характеристики под твою цель', edit: 'Добавь в сравнение:' }],
  explain_topic: [{ text: 'один пример из жизни', edit: 'Замени пример:' }],
  homework_help: [{ text: 'сначала ответ, потом шаги', edit: 'Дай подсказки без готового ответа:' }],
  sale_ad: [{ text: 'заголовок и текст отдельно', edit: 'Перепиши объявление для:' }],
}

/** Общие строки блока — таблица текстов, ключи `assumptions.*`. */
export const ASSUMPTION_TEXT = {
  heading: 'Исходил из:',
  edit: 'Изменить',
  collapse: 'Свернуть',
  hint: 'Выбери, что изменить, — допиши в поле и отправь.',
} as const

/**
 * Цена правки, когда она спишется: серая строка под блоком и подпись кнопки
 * «Изменить» для чтения с экрана (`assumptions.paid.note`,
 * `assumptions.edit.paid.a11y` — один текст). Кнопка остаётся «Изменить»:
 * «Изменить за 1 документ» без «с лимита» на iOS читается ценником, а целиком
 * в строку не влезает (ворота ux-writer-critic, 27.10.2026).
 */
export const assumptionPaidNote = (unit: UnitId): string => `Изменить — новая задача, спишется ${formatUnit(1, unit)} с лимита`

/** Подпись пункта для чтения с экрана, когда правка спишется. */
export const assumptionPaidA11y = (a: Assumption): string => `Изменить новой задачей: ${a.text}`

/**
 * Спишется ли правка допущения — и чем; null — уйдёт бесплатно (В4-Б).
 *
 * Бесплатно правка уходит, если у результата есть бесплатная правка
 * (`freeEdit` — экран её уже обещает строкой под полем) или в чате остались
 * бесплатные уточнения (`freeLeft`, общий запас, п. 6.4 правил). Решает
 * `nextSendCost` — то же правило, по которому строка под полем пишет
 * «Спишется … с лимита». Единица — ответа под блоком: правку той же
 * задачи классификатор почти всегда относит к тому же типу; разойдётся —
 * окно подтверждения назовёт единицу, которую спишет сервер (принято
 * владельцем 27.10.2026).
 */
export function assumptionPaidUnit(freeEdit: boolean, freeLeft: number, unit: UnitId | undefined): UnitId | null {
  return nextSendCost(freeEdit, freeLeft) === 'charged' && unit != null ? unit : null
}

/**
 * Единица строки цены под полем. Пока в поле правка допущения (`assumption` —
 * единица ответа под блоком) и задача не выбрана карточкой или уточнением
 * (`taskChosen`) — она, как в серой строке блока: иначе над полем «спишется
 * 1 документ», а под ним «Спишется 1 вопрос» (ворота ux-writer-critic,
 * 27.10.2026). Иначе — `next`, единица выбранного или свободного ввода.
 */
export const assumptionFieldUnit = (assumption: UnitId | undefined, taskChosen: boolean, next: UnitId): UnitId =>
  !taskChosen && assumption != null ? assumption : next

/** Строки блока типа; пусто — блока нет (тип не определён или без строк). */
export const assumptionsFor = (type: TaskTypeId | null | undefined): readonly Assumption[] =>
  (type != null ? ASSUMPTIONS[type] : undefined) ?? []

/** Строка под ответом без заголовка: «закон о правах потребителей; …». */
export const assumptionLine = (list: readonly Assumption[]): string => list.map((a) => a.text).join('; ')

/** Подпись пункта для чтения с экрана (`assumptions.item.a11y`). */
export const assumptionA11y = (a: Assumption): string => `Изменить: ${a.text}`

/**
 * Что встанет в поле по нажатию пункта (Р2, 26.10.2026): правилом поля из ядра
 * (`appendToField`) — набранное не стирается, заготовка встаёт новым абзацем и
 * второй раз не дописывается. Пробел в конце — курсор сразу для дописки.
 */
export const withAssumptionEdit = (field: string, a: Assumption): string => appendToField(field, `${a.edit} `)

/** Запуск в ленте — то, что нужно правилу места блока. */
export interface AssumptionRun {
  runId: string
  taskType?: TaskTypeId
  charged: boolean
  outcome?: string
}

/**
 * Под каким ответом чата стоит блок; null — ни под каким (Р1, 26.10.2026).
 *
 * Только под ответом последней СПИСАННОЙ задачи чата — той же функцией
 * (`lastChargedIndex`), на которой стоит запас бесплатных обращений
 * (`isCurrentResult`): блок
 * под ответом, правку которого сервер счёл бы новой задачей, обещал бы не то.
 * Ответы на «Изменить» (правка, бесплатное уточнение) не списаны и места не
 * занимают, даже если у уточнения chat-типа тип записан. Списанный запуск без
 * готового ответа или без строк показа — блока нет нигде. `runs` — в порядке
 * ленты.
 */
export function assumptionsRunId(runs: readonly AssumptionRun[]): string | null {
  const last = runs[lastChargedIndex(runs)]
  return last != null && last.outcome === 'done' && assumptionsFor(last.taskType).length > 0 ? last.runId : null
}

/** Сообщение ленты — то, что нужно выбору места блока. */
export interface AssumptionMessage {
  id: string
  runId?: string | null | undefined
  role: string
  kind?: string | undefined
}

/**
 * Где стоит блок: запуск (`assumptionsRunId`), его последнее сообщение-ответ
 * ассистента — не плашка темы после ответа — и строки типа. Одна функция на
 * оба клиента (ворота T4/T5, dedup-hunter): разойдись выбор сообщения, блок
 * встал бы под разными сообщениями на двух платформах. Циклом с конца, а не
 * `findLast`: Hermes знает его не во всех сборках.
 */
export function assumptionsPlace(
  runs: readonly AssumptionRun[],
  messages: readonly AssumptionMessage[],
): { runId: string; messageId: string; list: readonly Assumption[] } | null {
  const runId = assumptionsRunId(runs)
  if (runId == null) return null
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m != null && m.runId === runId && m.role === 'assistant' && m.kind !== 'notice') {
      return { runId, messageId: m.id, list: assumptionsFor(runs.find((r) => r.runId === runId)?.taskType) }
    }
  }
  return null
}
